/**
 * @voltxampere/ui — WaveformDirtyTracker (Sprint 15)
 *
 * Channel-scoped variant of the schematic `DirtyTracker`. The
 * waveform renderer redraws either (a) every visible channel on
 * a viewport change (time base / V/div / pan) or (b) just the
 * channels whose sample buffers grew since the last frame. This
 * keeps live mode cheap — at 60 fps the typical tick mutates one
 * channel's buffer by a single sample, so 5/6 of the panel can
 * skip the redraw.
 *
 * Mirrors the schematic tracker's "consume + reset" contract so
 * the renderer's dispatch loop reads the same way for both
 * domains.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */
/** Folding threshold — past this many channels we just full-redraw. */
export const MAX_DIRTY_CHANNELS = 6;
export class WaveformDirtyTracker {
    dirtyChannels = new Set();
    fullRedrawRequested = false;
    /** Mark one channel for redraw on the next frame. Folds into a
     *  full redraw once every channel is dirty. */
    markChannel(id) {
        if (this.fullRedrawRequested)
            return;
        this.dirtyChannels.add(id);
        if (this.dirtyChannels.size >= MAX_DIRTY_CHANNELS) {
            this.fullRedrawRequested = true;
            this.dirtyChannels.clear();
        }
    }
    /** Schedule a full redraw — used on viewport change, theme swap,
     *  trigger config change, channel toggle. */
    markAllDirty() {
        this.fullRedrawRequested = true;
        this.dirtyChannels.clear();
    }
    /** Snapshot pending state and reset internal counters. */
    consume() {
        const result = {
            full: this.fullRedrawRequested,
            channels: this.dirtyChannels,
        };
        this.dirtyChannels = new Set();
        this.fullRedrawRequested = false;
        return result;
    }
    /** True when nothing is pending. The renderer skips its frame. */
    get isClean() {
        return !this.fullRedrawRequested && this.dirtyChannels.size === 0;
    }
    get pendingCount() {
        return this.fullRedrawRequested ? -1 : this.dirtyChannels.size;
    }
}
//# sourceMappingURL=dirty.js.map